import React, { useState, useEffect, useCallback } from 'react';
import { LayoutTemplate, Sparkles, Inbox, BadgeDollarSign, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import AdminAdSpaces from './AdminAdSpaces';
import AdminHousePromos from './AdminHousePromos';
import AdminAdRequests from './AdminAdRequests';
import AdminAdInvitations from './AdminAdInvitations';
import AdminSpotlightPromotions from './AdminSpotlightPromotions';

/**
 * Admin · Advertising (rebuilt session 62 at his request — the area was hard to navigate).
 *
 * Four vertical blocks instead of one long linear page, the way the coaching program reads:
 *   Ad Spaces · My Promos · Requests · Paid Ads
 */
const BLOCKS = [
  {
    key: 'spaces', title: 'Ad Spaces', icon: LayoutTemplate,
    accent: 'text-cyan-300', ring: 'border-cyan-400/25',
    blurb: 'Every space on the platform: what is running, what it costs, what is empty.',
  },
  {
    key: 'promos', title: 'My Promos', icon: Sparkles,
    accent: 'text-amber-300', ring: 'border-amber-400/25',
    blurb: 'Your own promotions — create, schedule and reorder them.',
  },
  {
    key: 'requests', title: 'Requests', icon: Inbox,
    accent: 'text-emerald-300', ring: 'border-emerald-400/25',
    blurb: 'People asking for a space. You decide who gets offered one.',
  },
  {
    key: 'paid', title: 'Paid Ads', icon: BadgeDollarSign,
    accent: 'text-violet-300', ring: 'border-violet-400/25',
    blurb: 'Invitations you have sent and ads waiting for your approval.',
  },
];

export default function AdminSpotlightsTab() {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const [open, setOpen] = useState('spaces');
  const [fillSlot, setFillSlot] = useState(null);
  const [invitePrefill, setInvitePrefill] = useState(null);
  const [counts, setCounts] = useState({ requests: 0, paid: 0 });

  const loadCounts = useCallback(async () => {
    try {
      const [req, pending] = await Promise.all([
        axios.get(`${API}/admin/ad-invitations/requests?status=open`, { headers }),
        axios.get(`${API}/admin/spotlight-promotions?status=paid_pending_approval`, { headers }),
      ]);
      setCounts({
        requests: req.data.open_count || 0,
        paid: (pending.data.promotions || []).length,
      });
    } catch { /* the badges are a nicety, not worth an error toast */ }
  }, []);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  // "Put a promo here" jumps into My Promos with that space pre-selected.
  const fillSpace = (slotKey) => { setFillSlot(slotKey); setOpen('promos'); };
  // "Invite them to advertise" jumps into Paid Ads with their details filled in.
  const inviteFromRequest = (request) => {
    setInvitePrefill({ ...request, _ts: Date.now() });
    setOpen('paid');
    loadCounts();
  };

  const body = (key) => {
    if (key === 'spaces') return <AdminAdSpaces onFillSpace={fillSpace} />;
    if (key === 'promos') return <AdminHousePromos fillSlot={fillSlot} onFillConsumed={() => setFillSlot(null)} />;
    if (key === 'requests') return <AdminAdRequests onInvite={inviteFromRequest} />;
    return (
      <div className="space-y-5">
        <AdminAdInvitations prefill={invitePrefill} />
        <AdminSpotlightPromotions />
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="admin-spotlights-tab">
      <div>
        <h3 className="text-lg font-semibold text-gray-100 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-300" /> Advertising
        </h3>
        <p className="text-sm text-gray-400">
          Featured and sponsored placements across Marketplace, Workshops &amp; Bookings.
        </p>
      </div>

      {BLOCKS.map(({ key, title, icon: Icon, accent, ring, blurb }) => {
        const isOpen = open === key;
        const badge = counts[key];
        return (
          <div key={key}
            className={`rounded-2xl border ${isOpen ? ring : 'border-white/10'} bg-white/[0.03] backdrop-blur-xl overflow-hidden transition-colors`}
            data-testid={`ad-block-${key}`}>
            <button type="button"
              onClick={() => setOpen(isOpen ? null : key)}
              className="w-full flex items-center gap-4 p-5 text-left hover:bg-white/[0.03] transition-colors"
              data-testid={`ad-block-toggle-${key}`}>
              <span className={`w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center ${accent}`}>
                <Icon className="w-5 h-5" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-2">
                  <span className="text-base font-semibold text-gray-100">{title}</span>
                  {badge > 0 && (
                    <span className={`text-[10px] px-2 py-0.5 rounded-full bg-white/10 ${accent}`}
                      data-testid={`ad-block-count-${key}`}>
                      {badge}
                    </span>
                  )}
                </span>
                <span className="block text-xs text-gray-400 mt-0.5">{blurb}</span>
              </span>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && <div className="px-5 pb-5 border-t border-white/5 pt-4">{body(key)}</div>}
          </div>
        );
      })}
    </div>
  );
}
